import React, { useState } from "react";
import {
  Stack,
  Title,
  Text,
  Card,
  Group,
  ThemeIcon,
  SimpleGrid,
  Button,
  Collapse,
  UnstyledButton,
  ActionIcon,
} from "@mantine/core";
import {
  IconCurrencyDollar,
  IconPlus,
  IconChevronDown,
  IconChevronRight,
  IconPencil,
} from "@tabler/icons-react";

const SCALE_TYPE_ORDER = ["COMMISSION", "MD", "OVERRIDE"];

const SCALE_TYPE_LABELS = {
  COMMISSION: "Commission",
  MD: "MD",
  OVERRIDE: "Override",
};

const formatRevenue = amount => {
  const value = Number(amount) || 0;
  if (value === 0) return "$0";
  return `$${value.toLocaleString("en-US")}+`;
};

const formatPercent = percentage =>
  percentage == null ? "0%" : `${percentage}%`;

function ScaleCard({ scale, onEdit }) {
  const lines = [...(scale.scale_lines || [])].sort(
    (a, b) => (a.revenue_amount || 0) - (b.revenue_amount || 0)
  );

  return (
    <Card withBorder radius="md" padding={0} style={{ overflow: "hidden" }}>
      {/* Card header - scale name */}
      <Group justify="space-between" px="md" py="sm" bg="#ffffff" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon color="orange" variant="light" size={28} radius="md">
            <IconCurrencyDollar size={18} />
          </ThemeIcon>
          <Text fw={700} size="sm" c="#333" truncate>
            {scale.name}
          </Text>
        </Group>
        {onEdit && (
          <ActionIcon
            variant="light"
            color="gray"
            onClick={() => onEdit(scale)}
            aria-label={`Edit ${scale.name}`}
          >
            <IconPencil size={16} />
          </ActionIcon>
        )}
      </Group>

      {/* Column headers */}
      <Group
        justify="space-between"
        px="md"
        py="xs"
        bg="#F4F1EA"
        style={{ borderTop: "1px solid #ece0d8" }}
      >
        <Text size="xs" fw={700} c="#6b5a50">
          Retained Revenue
        </Text>
        <Text size="xs" fw={700} c="#6b5a50">
          {scale.scale_type} %
        </Text>
      </Group>

      {/* Rows */}
      {lines.map((line, index) => (
        <Group
          key={line.id ?? index}
          justify="space-between"
          px="md"
          py="xs"
          bg={index % 2 === 0 ? "#ffffff" : "#F4F1EA"}
          style={{ borderTop: "1px solid #f2eae4" }}
        >
          <Text size="sm" fw={600} c="#333">
            {formatRevenue(line.revenue_amount)}
          </Text>
          <Text size="sm" fw={700} c="#b9491c">
            {formatPercent(line.percentage)}
          </Text>
        </Group>
      ))}
    </Card>
  );
}

function ScalesSection({
  scales,
  title = "Scales",
  onAdd,
  addLabel = "Add Scale",
  onEdit,
}) {
  const [open, setOpen] = useState(true);

  // scales is keyed by scale type (COMMISSION / OVERRIDE / MD), each an array.
  const hasAny = SCALE_TYPE_ORDER.some(
    type => Array.isArray(scales?.[type]) && scales[type].length > 0
  );

  return (
    <Stack gap="md" mt={48}>
      <Group justify="space-between" align="center">
        <UnstyledButton onClick={() => setOpen(o => !o)}>
          <Group gap="xs" align="center">
            {open ? (
              <IconChevronDown size={18} />
            ) : (
              <IconChevronRight size={18} />
            )}
            <Title order={4}>{title}</Title>
          </Group>
        </UnstyledButton>
        {onAdd && (
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={onAdd}
          >
            {addLabel}
          </Button>
        )}
      </Group>

      <Collapse in={open}>
        {!hasAny ? (
          <Text size="sm" c="dimmed">
            No scales configured.
          </Text>
        ) : (
          <Stack gap="md">
            {SCALE_TYPE_ORDER.map(type => {
              const typeScales = Array.isArray(scales?.[type])
                ? scales[type]
                : [];
              if (typeScales.length === 0) return null;
              return (
                <Stack key={type} gap="sm">
                  <Title order={5}>{SCALE_TYPE_LABELS[type] || type}</Title>
                  <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="sm">
                    {typeScales.map(scale => (
                      <ScaleCard key={scale.id} scale={scale} onEdit={onEdit} />
                    ))}
                  </SimpleGrid>
                </Stack>
              );
            })}
          </Stack>
        )}
      </Collapse>
    </Stack>
  );
}

export default ScalesSection;
